/**
 * Error envelope returned by the LaunchPoint Portal API on a handled error.
 *
 * The API serializes responses in PascalCase (no camelCase contract resolver is
 * registered), so these property names match the wire exactly. Rendered by the
 * server-side `ApiExceptionFilterAttribute` from `ApiErrorCatalog`.
 */
export interface ApiErrorResponse {
    /** Stable machine-readable code, e.g. "PAYRATE_DUPLICATE_EFFECTIVE_DATE". */
    ErrorCode: string;
    /** Plain-language explanation of why the request failed. */
    Message: string;
    /** Plain-language suggestion for how to resolve or proceed. */
    Suggestion: string;
    /** HTTP status code (mirrors the response status). */
    Status: number;
    /** Correlation id to quote to support; ties this response to the server-side log. */
    CorrelationId: string;
}

interface Vault77ApiErrorDetails {
    errorCode?: string | null;
    suggestion?: string | null;
    status?: number | null;
    correlationId?: string | null;
}

/**
 * Error thrown by the vault77 API layer. When the server returned the standard
 * {@link ApiErrorResponse} envelope, its fields are carried here so the UI can show
 * the reason, a suggestion, and a support correlation id instead of a bare status.
 */
export class Vault77ApiError extends Error {
    readonly errorCode: string | null;
    readonly suggestion: string | null;
    readonly status: number | null;
    readonly correlationId: string | null;

    constructor(message: string, details?: Vault77ApiErrorDetails) {
        super(message);
        this.name = "Vault77ApiError";
        this.errorCode = details?.errorCode ?? null;
        this.suggestion = details?.suggestion ?? null;
        this.status = details?.status ?? null;
        this.correlationId = details?.correlationId ?? null;
        // Required when extending built-ins under TS's ES5 target so `instanceof` works.
        Object.setPrototypeOf(this, Vault77ApiError.prototype);
    }

    /**
     * Human-facing string combining the reason, suggestion, and (for server faults) a support
     * reference. The reference is only shown for 5xx because the API logs 5xx to Datadog at Error
     * level; 4xx are logged at Information and won't be in Datadog, so quoting a reference for them
     * would be a dead end for support.
     */
    toDisplayString(): string {
        let text = this.message;
        if (this.suggestion) text += ` ${this.suggestion}`;
        if (this.correlationId && this.status != null && this.status >= 500) {
            text += ` (Reference: ${this.correlationId})`;
        }
        return text;
    }
}

function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
    if (typeof data !== "object" || data === null) return false;
    const candidate = data as Partial<ApiErrorResponse>;
    return typeof candidate.ErrorCode === "string" && typeof candidate.Message === "string";
}

/**
 * Convert any thrown value (typically an Axios error) into a {@link Vault77ApiError}.
 * If the response carries the API error envelope, its Message/Suggestion/etc. are used;
 * otherwise the supplied fallback message is used (with the HTTP status when available).
 *
 * Duck-types `error.response` rather than relying on `Axios.isAxiosError` — that helper does
 * not exist in axios 0.19.x (this project's version), which would otherwise drop the envelope.
 */
export function toVault77ApiError(error: unknown, fallbackMessage: string): Vault77ApiError {
    const response = (error as { response?: { status?: number; data?: unknown } } | undefined)?.response;
    if (response) {
        const data = response.data;
        if (isApiErrorResponse(data)) {
            return new Vault77ApiError(data.Message || fallbackMessage, {
                errorCode: data.ErrorCode,
                suggestion: data.Suggestion,
                status: data.Status ?? response.status ?? null,
                correlationId: data.CorrelationId,
            });
        }
        return new Vault77ApiError(fallbackMessage, { status: response.status ?? null });
    }
    return new Vault77ApiError(fallbackMessage);
}

/**
 * Convenience for hooks: derive a user-facing display message from any caught error.
 * A {@link Vault77ApiError} contributes its reason + suggestion + reference; any other
 * Error contributes its message; everything else falls back to `fallbackMessage`.
 */
export function getVault77ErrorMessage(error: unknown, fallbackMessage: string): string {
    if (error instanceof Vault77ApiError) return error.toDisplayString();
    if (error instanceof Error && error.message) return error.message;
    return fallbackMessage;
}
